import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { setVariantUrlParam } from '@/utils/products';
import { formatMoney } from '@/utils/formatting';
import { QuantitySelector } from '@/components/QuantitySelector/QuantitySelector';
import { ProductOptionPicker } from './ProductOptionPicker';
import { FakeDropdown } from '../FakeDropdown/FakeDropdown';
import { Component, Prop, Ref, SimpleEvent, Watch } from '@eastsideco/salvo-ts';
import { debounce } from 'lodash';
import { scrollToElement } from '@/utils/scroll';

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop defaultVariant!: Shopify.Liquid.Variant;

    @Ref({ required: false }) options!: ProductOptionPicker;
    @Ref() quantitySelector!: QuantitySelector;
    @Ref() actions!: HTMLDivElement;
    @Ref({ required: false }) purchaseOption!: HTMLInputElement[];
    @Ref({ required: false }) deliveryFrequency!: FakeDropdown[];
    @Ref({ required: false }) cancelSubscriptionNotification!: HTMLElement;
    @Ref() submitButton!: HTMLButtonElement[];
    @Ref() addToCartPrice!: HTMLElement[];
    @Ref error!: HTMLElement;
    @Ref errorMessage!: HTMLElement;
    @Ref({ required: false }) stickyBanner!: HTMLElement;
    @Ref({ required: false }) editButton!: HTMLElement;

    protected selectedVariant: Shopify.Liquid.Variant | null = null;
    protected quantity: number = 1;
    protected selectedSellingPlanId: number | undefined = undefined;
    private mobileBreakpoint: number = 768;

    init() {

        // Set default variant
        this.selectedVariant = this.defaultVariant;

        // Check variant availability
        this.checkVariantAvailability();

        // Listen for quantity change
        this.listenForQuantityChange();

        // Listen for variant change
        this.listenForVariantChange();

        // Get initial selected selling plan
        this.selectedSellingPlanId = this.getSelectedSellingPlanId();

        // Update add to cart price
        this.updateAddToCartPrice();

        // Listen for selling plan change
        this.listenForPurchaseTypeChange();

        // Listen for delivery frequency change
        this.listenForDeliveryFrequencyChange();

        // Add sticky banner event listeners
        this.addStickyBannerEventListeners();

        // Listen for form submission
        this.listenForFormSubmission();
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
    }

    private _onSelectedPropertyChange = new SimpleEvent<string>();
    public get onSelectedPropertyChange() {
        return this._onSelectedPropertyChange.asEvent();
    }

    checkVariantAvailability() {

        // Check variant is defined
        if (this.selectedVariant === null) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Check variant is in stock
        if (!this.selectedVariant.available) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Enable
        this.updateActionsVisibility(false);
    }

    listenForQuantityChange() {
        this.$subscribe(this.quantitySelector.onChange, (quantity) => {

            // Hide error
            this.error.classList.remove('product-form__error--active');

            // Update quantity
            this.quantity = quantity;

            // Update add to cart price
            this.updateAddToCartPrice();
        });
    }

    listenForVariantChange() {
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
                if (variant === null) {
                    return;
                }
                this.selectedVariant = variant;

                // Check variant availability
                this.checkVariantAvailability();

                // Update add to cart price
                this.updateAddToCartPrice();

                // Hide error
                this.error.classList.remove('product-form__error--active');
            });
            this.selectedVariant = this.options.selectedVariant;
        }
    }

    listenForPurchaseTypeChange() {
        for (const purchaseOption of this.purchaseOption) {
            this.$listen(purchaseOption, 'change', (e) => {

                // Toggle selected classes
                for (const otherPurchaseOption of this.purchaseOption) {
                    const parent = otherPurchaseOption.closest('.product-form__purchase-types__item');
                    if (otherPurchaseOption === purchaseOption) {
                        parent?.classList.add('product-form__purchase-types__item--selected');
                    } else {
                        parent?.classList.remove('product-form__purchase-types__item--selected');
                    }
                }

                // Get value
                const value = Number(purchaseOption.value);

                // Update selling plan ID
                if (value === 0) {
                    this.selectedSellingPlanId = undefined;
                } else {
                    this.selectedSellingPlanId = this.getSelectedSellingPlanId();
                }

                // Update delivery frequency value and visibility
                this.updateDeliveryFrequencyValueAndVisibility();

                // Update add to cart price
                this.updateAddToCartPrice();
            });
        }
    }

    getSelectedSellingPlanId() {
        let selectedSellingPlanId = undefined;
        for (const deliveryFrequency of this.deliveryFrequency) {
            selectedSellingPlanId = deliveryFrequency.selectedOptionValue;
        }
        if (typeof (selectedSellingPlanId) !== 'undefined') {
            selectedSellingPlanId = Number(selectedSellingPlanId);
        }
        return selectedSellingPlanId;
    }

    updateDeliveryFrequencyValueAndVisibility() {
        if (typeof (this.selectedSellingPlanId) === 'undefined') {
            for (const deliveryFrequency of this.deliveryFrequency) {
                deliveryFrequency.classList.add('product-form__purchase-types__delivery--hidden');
            }
            if (this.cancelSubscriptionNotification) {
                // eslint-disable-next-line max-len
                this.cancelSubscriptionNotification.classList.add('product-form__cancel-subscription-notification--hidden');
            }
        } else {
            for (const deliveryFrequency of this.deliveryFrequency) {
                deliveryFrequency.classList.remove('product-form__purchase-types__delivery--hidden');
            }
            if (this.cancelSubscriptionNotification) {
                // eslint-disable-next-line max-len
                this.cancelSubscriptionNotification.classList.remove('product-form__cancel-subscription-notification--hidden');
            }
        }
    }

    listenForDeliveryFrequencyChange() {
        for (const deliveryFrequency of this.deliveryFrequency) {
            this.$subscribe(deliveryFrequency.onChange, (value) => {
                this.selectedSellingPlanId = Number(value);

                // Update add to cart price
                this.updateAddToCartPrice();
            });
        }
    }

    updateAddToCartPrice() {

        // Ensure variant is selected
        if (this.selectedVariant) {

            // Get price of variant * quantity
            let price = this.selectedVariant.price * this.quantity;

            // Check if selling plan offers a discount
            if (typeof this.selectedSellingPlanId !== 'undefined') {
                const discount_percentage_attribute = this.addToCartPrice[0].getAttribute('data-selling-plan-discount');
                if (discount_percentage_attribute) {
                    const discount_percentage = Number(discount_percentage_attribute);
                    price = price - (price * (discount_percentage / 100));
                }
            }

            // Update price
            for (const addToCartPrice of this.addToCartPrice) {
                addToCartPrice.textContent = formatMoney(price);
            }
        }
    }

    listenForFormSubmission() {
        for (const submitButton of this.submitButton) {
            this.$listen(submitButton, 'click', () => {
                this.addToCart().catch((e) => {
                    this.$log.error(`Add to cart failed`, { e });
                });
            });
        }
    }

    updateActionsVisibility(disabled: boolean) {

        // Disable button
        if (disabled) {
            for (const submitButton of this.submitButton) {
                submitButton.classList.add('product-form__submit--disabled');
            }

            // Enable button
        } else {
            for (const submitButton of this.submitButton) {
                submitButton.classList.remove('product-form__submit--disabled');
            }
        }
    }

    protected async addToCart() {
        try {

            // Check variant exists
            const variant = this.selectedVariant;
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // Add to cart
            await cart.addItem({
                id: variant.id,
                quantity: this.quantity,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                selling_plan: this.selectedSellingPlanId
            });

            // Dispatch event for other components to listen to
            document.dispatchEvent(new Event('cartItemAdded'));
        } catch (event) {
            this.$log.error('Cart error', {
                event
            });
            const { message } = event as Error;
            this.showError(message);
        }
    }

    protected showError(msg: string | null): void {
        this.errorMessage.textContent = msg;
        if (msg) {
            this.error.classList.add('product-form__error--active');
        } else {
            this.error.classList.remove('product-form__error--active');
        }
    }

    private addStickyBannerEventListeners() {

        // Ensure sticky banner is rendered
        if (!this.stickyBanner) {
            return;
        }

        // Listen for scroll - handle sticky banner visibility
        this.$listen(window, 'scroll', debounce(() => this.handleScroll()));
        this.handleScroll();

        // Listen for edit button click
        this.listenForEditButtonClick();
    }

    private handleScroll() {

        // Determine element to watch scroll position
        let additionalThreshold = window.innerHeight;
        let elementToWatch: HTMLElement|null = this as HTMLElement;
        if (window.innerWidth > this.mobileBreakpoint) {
            elementToWatch = document.querySelector('.product-main__gallery-details');
            if (elementToWatch === null) {
                elementToWatch = this as HTMLElement;
            }
            additionalThreshold = 0;
        }

        // Get element top position
        const topPosition = elementToWatch.offsetTop;

        // Get element height
        const height = elementToWatch.offsetHeight;

        // Determine scroll threshold
        const scrollThreshold = topPosition + height + additionalThreshold;

        // Check if we have scrolled to the element
        if (window.pageYOffset >= scrollThreshold) {

            // Remove hidden class
            this.stickyBanner.classList.remove('product-form__sticky-banner--hidden');
        } else {

            // Add hidden class
            this.stickyBanner.classList.add('product-form__sticky-banner--hidden');
        }
    }

    private listenForEditButtonClick() {

        // Ensure edit button is rendered
        if (!this.editButton) {
            return;
        }

        // Determine element to scroll to
        let elementToScrollTo: HTMLElement|null = this as HTMLElement;
        if (window.innerWidth > this.mobileBreakpoint) {
            elementToScrollTo = document.querySelector('.product-main__gallery-details');
            if (elementToScrollTo === null) {
                elementToScrollTo = this as HTMLElement;
            }
        }

        // Listen for click, scroll to element
        this.$listen(this.editButton, 'click', () => {
            scrollToElement(elementToScrollTo);
        });
    }
}

